// ============================================================
// BigQuery load jobs (service account, no SDK)
//
//   serviceAccountToken   RS256 JWT → OAuth access token (WebCrypto)
//   BigQueryLoader        resumable media upload → one load job per partition
//
// Load jobs are atomic: a failed job loads nothing, so a failed hour is
// retried with the next attempt's job id; a duplicate job id (409) means the
// hour was already submitted and is never loaded twice.
// ============================================================
import { fromBase64Url, toBase64Url } from '../crypto.js';
import { bigQuerySchema } from './rows.js';
import { globalFetch } from './s3.js';
const enc = new TextEncoder();
/** Always Google's token endpoint: a key file's `token_uri` is customer input (SSRF). */
export const GOOGLE_TOKEN_URI = 'https://oauth2.googleapis.com/token';
export class WarehouseAuthError extends Error {
}
export function parseServiceAccount(json) {
    let sa;
    try {
        sa = JSON.parse(json);
    }
    catch {
        throw new WarehouseAuthError('service_account_json is not valid JSON');
    }
    if (typeof sa.client_email !== 'string' ||
        typeof sa.private_key !== 'string' ||
        !sa.private_key.includes('PRIVATE KEY')) {
        throw new WarehouseAuthError('service_account_json needs client_email and private_key (a Google service-account key file)');
    }
    return {
        client_email: sa.client_email,
        private_key: sa.private_key,
        project_id: sa.project_id,
    };
}
function pemToPkcs8(pem) {
    const b64 = pem
        .replace(/-----BEGIN [A-Z ]+-----/, '')
        .replace(/-----END [A-Z ]+-----/, '')
        .replace(/\s+/g, '');
    return fromBase64Url(b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''));
}
export async function signServiceAccountJwt(sa, scope, nowSeconds = Math.floor(Date.now() / 1000)) {
    const header = toBase64Url(enc.encode(JSON.stringify({ alg: 'RS256', typ: 'JWT' })));
    const claims = toBase64Url(enc.encode(JSON.stringify({
        iss: sa.client_email,
        scope,
        aud: GOOGLE_TOKEN_URI,
        iat: nowSeconds,
        exp: nowSeconds + 3600,
    })));
    let key;
    try {
        key = await crypto.subtle.importKey('pkcs8', pemToPkcs8(sa.private_key), { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' }, false, ['sign']);
    }
    catch {
        throw new WarehouseAuthError('service account private_key could not be imported');
    }
    const sig = await crypto.subtle.sign('RSASSA-PKCS1-v1_5', key, enc.encode(`${header}.${claims}`));
    return `${header}.${claims}.${toBase64Url(new Uint8Array(sig))}`;
}
export const BIGQUERY_SCOPE = 'https://www.googleapis.com/auth/bigquery';
export async function serviceAccountToken(sa, fetchImpl = globalFetch, scope = BIGQUERY_SCOPE) {
    const assertion = await signServiceAccountJwt(sa, scope);
    const resp = await fetchImpl(GOOGLE_TOKEN_URI, {
        method: 'POST',
        headers: { 'content-type': 'application/x-www-form-urlencoded' },
        body: `grant_type=${encodeURIComponent('urn:ietf:params:oauth:grant-type:jwt-bearer')}&assertion=${assertion}`,
    });
    const body = (await resp.json().catch(() => ({})));
    if (!resp.ok || !body.access_token) {
        throw new WarehouseAuthError(`Google token exchange failed: ${body.error_description ?? body.error ?? `HTTP ${resp.status}`}`);
    }
    return body.access_token;
}
/** Resumable-upload chunks must be multiples of 256 KiB (except the last). */
export const BQ_CHUNK_BYTES = 8 * 1024 * 1024;
const API = 'https://bigquery.googleapis.com/bigquery/v2';
const UPLOAD = 'https://bigquery.googleapis.com/upload/bigquery/v2';
export class BigQueryError extends Error {
    status;
    constructor(message, status) {
        super(message);
        this.status = status;
    }
}
async function errorText(resp) {
    const body = (await resp.json().catch(() => null));
    return body?.error?.message?.slice(0, 300) ?? `HTTP ${resp.status}`;
}
export class BigQueryLoader {
    target;
    token;
    fetchImpl;
    constructor(target, token, fetchImpl = globalFetch) {
        this.target = target;
        this.token = token;
        this.fetchImpl = fetchImpl;
    }
    auth(extra = {}) {
        return { authorization: `Bearer ${this.token}`, ...extra };
    }
    /** Dataset must exist and be visible to the service account. */
    async checkDataset() {
        const t = this.target;
        const resp = await this.fetchImpl(`${API}/projects/${encodeURIComponent(t.projectId)}/datasets/${encodeURIComponent(t.dataset)}`, { headers: this.auth() });
        if (!resp.ok) {
            throw new BigQueryError(`dataset check failed: ${await errorText(resp)}`, resp.status);
        }
    }
    /**
     * Start a load job with a resumable upload. Returns `null` when the job id
     * already exists (the partition was submitted before).
     */
    async begin(jobId) {
        const t = this.target;
        const job = {
            jobReference: { projectId: t.projectId, jobId, location: t.location },
            configuration: {
                load: {
                    destinationTable: {
                        projectId: t.projectId,
                        datasetId: t.dataset,
                        tableId: t.table,
                    },
                    sourceFormat: 'NEWLINE_DELIMITED_JSON',
                    schema: { fields: bigQuerySchema() },
                    writeDisposition: 'WRITE_APPEND',
                    createDisposition: 'CREATE_IF_NEEDED',
                    timePartitioning: { type: 'DAY', field: 'event_timestamp' },
                    clustering: { fields: ['event', 'sub_tenant'] },
                    schemaUpdateOptions: ['ALLOW_FIELD_ADDITION'],
                },
            },
        };
        const resp = await this.fetchImpl(`${UPLOAD}/projects/${encodeURIComponent(t.projectId)}/jobs?uploadType=resumable`, {
            method: 'POST',
            headers: this.auth({
                'content-type': 'application/json; charset=UTF-8',
                'x-upload-content-type': 'application/octet-stream',
            }),
            body: JSON.stringify(job),
        });
        if (resp.status === 409) {
            return null;
        }
        const location = resp.headers.get('location');
        if (!resp.ok || !location) {
            throw new BigQueryError(`load job start failed: ${await errorText(resp)}`, resp.status);
        }
        return new ResumableUpload(location, this.fetchImpl);
    }
    async jobState(jobId) {
        const t = this.target;
        const resp = await this.fetchImpl(`${API}/projects/${encodeURIComponent(t.projectId)}/jobs/${encodeURIComponent(jobId)}?location=${encodeURIComponent(t.location)}`, { headers: this.auth() });
        if (resp.status === 404) {
            return { state: 'failed', error: 'job not found' };
        }
        if (!resp.ok) {
            throw new BigQueryError(`job status failed: ${await errorText(resp)}`, resp.status);
        }
        const job = await resp.json();
        if (job.status?.state !== 'DONE') {
            return { state: 'running' };
        }
        if (job.status.errorResult) {
            return { state: 'failed', error: job.status.errorResult.message ?? 'load failed' };
        }
        return { state: 'done', rows: Number(job.statistics?.load?.outputRows ?? 0) };
    }
}
/** Streams NDJSON into a resumable session in 8 MiB chunks; memory stays bounded. */
export class ResumableUpload {
    sessionUrl;
    fetchImpl;
    chunkBytes;
    buf = [];
    bufBytes = 0;
    sent = 0;
    constructor(sessionUrl, fetchImpl, chunkBytes = BQ_CHUNK_BYTES) {
        this.sessionUrl = sessionUrl;
        this.fetchImpl = fetchImpl;
        this.chunkBytes = chunkBytes;
    }
    get bytes() {
        return this.sent + this.bufBytes;
    }
    async write(data) {
        this.buf.push(data);
        this.bufBytes += data.byteLength;
        while (this.bufBytes >= this.chunkBytes) {
            const all = concat(this.buf, this.bufBytes);
            const chunk = all.subarray(0, this.chunkBytes);
            const rest = all.subarray(this.chunkBytes);
            await this.put(chunk, false);
            this.buf = rest.byteLength ? [rest] : [];
            this.bufBytes = rest.byteLength;
        }
    }
    /** Sends the last chunk; returns once BigQuery has accepted the job. */
    async finish() {
        const last = concat(this.buf, this.bufBytes);
        this.buf = [];
        this.bufBytes = 0;
        return this.put(last, true);
    }
    async put(chunk, final) {
        const start = this.sent;
        const end = start + chunk.byteLength - 1;
        const total = final ? String(start + chunk.byteLength) : '*';
        const range = chunk.byteLength === 0 ? `bytes */${total}` : `bytes ${start}-${end}/${total}`;
        const resp = await this.fetchImpl(this.sessionUrl, {
            method: 'PUT',
            headers: { 'content-range': range },
            body: chunk.byteLength ? chunk : undefined,
        });
        this.sent += chunk.byteLength;
        if (resp.status === 409) {
            return 'duplicate';
        }
        if (!final && resp.status === 308) {
            return 'created';
        }
        if (final && resp.ok) {
            return 'created';
        }
        throw new BigQueryError(`upload chunk failed: ${await errorText(resp)}`, resp.status);
    }
}
function concat(parts, total) {
    if (parts.length === 1) {
        return parts[0];
    }
    const out = new Uint8Array(total);
    let o = 0;
    for (const p of parts) {
        out.set(p, o);
        o += p.byteLength;
    }
    return out;
}
//# sourceMappingURL=bigquery.js.map